import { useNavigation } from '@refinedev/core';
import { List, useTable, CreateButton } from '@refinedev/antd';
import { Table, Input, Space, Tag, Avatar, Button, Rate, Badge, Select } from 'antd';
import { SearchOutlined, UserOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { UserFilters, UserFilterValues } from '../../components/filters/UserFilters';

interface Driver {
  id: number;
  fullName: string | null;
  phone: string;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  isDriverVerified: boolean;
  createdAt: string;
  vehicles?: Array<{
    make: string;
    model: string;
    plate: string;
  }>;
}

export const DriverList = () => {
  const { show } = useNavigation();
  const [searchText, setSearchText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<UserFilterValues>({});

  const activeFilterCount = Object.values(activeFilters).filter(
    (v) => v !== undefined && v !== '' && v !== null
  ).length;

  const { tableProps, setFilters } = useTable<Driver>({
    resource: 'admin/drivers',
    syncWithLocation: true,
  });

  const handleApplyFilters = (filters: UserFilterValues) => {
    setActiveFilters(filters);

    const refineFilters: any[] = [];

    if (filters.search) {
      refineFilters.push({ field: 'search', operator: 'contains', value: filters.search });
    }
    if (filters.phone) {
      refineFilters.push({ field: 'phone', operator: 'contains', value: filters.phone });
    }
    if (filters.status) {
      refineFilters.push({ field: 'status', operator: 'eq', value: filters.status });
    }
    if (filters.isDriverVerified !== undefined) {
      refineFilters.push({ field: 'isDriverVerified', operator: 'eq', value: filters.isDriverVerified });
    }
    if (filters.minTrips !== undefined) {
      refineFilters.push({ field: 'minTrips', operator: 'gte', value: filters.minTrips });
    }
    if (filters.maxTrips !== undefined) {
      refineFilters.push({ field: 'maxTrips', operator: 'lte', value: filters.maxTrips });
    }
    if (filters.minRating !== undefined) {
      refineFilters.push({ field: 'minRating', operator: 'gte', value: filters.minRating });
    }
    if (filters.maxRating !== undefined) {
      refineFilters.push({ field: 'maxRating', operator: 'lte', value: filters.maxRating });
    }
    if (filters.registeredFrom) {
      refineFilters.push({ field: 'registeredFrom', operator: 'gte', value: filters.registeredFrom });
    }
    if (filters.registeredTo) {
      refineFilters.push({ field: 'registeredTo', operator: 'lte', value: filters.registeredTo });
    }
    if (filters.hasVehicle !== undefined) {
      refineFilters.push({ field: 'hasVehicle', operator: 'eq', value: filters.hasVehicle });
    }

    setFilters(refineFilters);
  };

  const handleResetFilters = () => {
    setActiveFilters({});
    setFilters([]);
  };

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'default',
    BLOCKED: 'red',
    RESTING: 'orange',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: 'Online',
    OFFLINE: 'Offline',
    BLOCKED: 'Bloklangan',
    RESTING: 'Dam olmoqda',
  };

  const columns = [
    {
      title: 'Haydovchi',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: string, record: Driver) => (
        <Space>
          <Avatar src={record.photoUrl} icon={<UserOutlined />} />
          <div>
            <div>{record.fullName || "Ismi yo'q"}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Tasdiqlangan',
      dataIndex: 'isDriverVerified',
      key: 'isDriverVerified',
      render: (verified: boolean) =>
        verified ? (
          <CheckCircleOutlined style={{ color: '#27AE60', fontSize: 18 }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#E74C3C', fontSize: 18 }} />
        ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Reyting',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number | string) => {
        const num = Number(rating) || 0;
        return (
          <Space>
            <Rate disabled defaultValue={num} count={5} style={{ fontSize: 12 }} />
            <span>{num.toFixed(1)}</span>
          </Space>
        );
      },
    },
    {
      title: 'Sayohatlar',
      dataIndex: 'totalTrips',
      key: 'totalTrips',
    },
    {
      title: "Ro'yxatdan o'tgan",
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: Driver) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/drivers', record.id)}
        />
      ),
    },
  ];

  return (
    <>
      <List
        title="Haydovchilar"
        headerButtons={
          <Space>
            <Input
              placeholder="Qidirish..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
              onPressEnter={() => handleApplyFilters({ ...activeFilters, search: searchText })}
            />
            <Select
              placeholder="Holat"
              allowClear
              style={{ width: 130 }}
              value={activeFilters.status}
              onChange={(value) => handleApplyFilters({ ...activeFilters, status: value })}
              options={[
                { value: 'ONLINE', label: 'Online' },
                { value: 'OFFLINE', label: 'Offline' },
                { value: 'RESTING', label: 'Dam olmoqda' },
                { value: 'BLOCKED', label: 'Bloklangan' },
              ]}
            />
            <Badge count={activeFilterCount} size="small">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterOpen(true)}
              >
                Filtr
              </Button>
            </Badge>
            <CreateButton>Yangi haydovchi</CreateButton>
          </Space>
        }
      >
        <Table {...tableProps} columns={columns} rowKey="id" />
      </List>

      <UserFilters
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        filterType="drivers"
        initialValues={activeFilters}
      />
    </>
  );
};
